import React from "react";
import { useRef, type FC } from "react";
import SplitTextAnimation from "../split-text-animation";
import { useInView } from "@/hooks/reveal-in-view";
import { ContentManager } from "../content-manager";
import { Image } from "../image";
import type { ContentfulCardWithImageType } from "@/types";
import type { EcosystemProps } from "@/types/components/home/ecosystem";

const EcosystemCard = ({card}: {card: ContentfulCardWithImageType}) => (
	<div className="ecosystem--card-wrapper">
		<div className="ecosystem--card">
			<div className="ecosystem--card--image-wrapper">
				{card.image?.file?.url && (
					<Image
						className="ecosystem--card-image"
						src={card.image as any}
						alt={card.image.title}
					/>
				)}
			</div>
			<div className="ecosystem--card--title-wrapper">
				{card.title && (
					<ContentManager
						content={card.title}
						className="ecosystem--card--title"
					/>
				)}
			</div>
		</div>
	</div>
)

const MissionAndVisionCard = ({card}: {card: ContentfulCardWithImageType}) => (
	<div className="ecosystem--mv-card-wrapper">
		<div className="ecosystem--mv-card">
			<div className="ecosystem--mv-card--image-wrapper">
				{card.image?.file?.url && (
					<Image
						className="ecosystem--mv-card--image"
						src={card.image as any}
						alt={card.image.title}
					/>
				)}
			</div>
			<div className="ecosystem--mv-card--title-wrapper">
				{card.title && (
					<ContentManager
						content={card.title}
						className="ecosystem--mv-card--title"
					/>
				)}
				{card.description && (
					<ContentManager
						content={card.description}
						className="ecosystem--mv-card--description"
					/>
				)}
			</div>
		</div>
	</div>
)

export const Ecosystem: FC<EcosystemProps> = ({
	subheader,
  header,
  ecosystemCards,
  missionAndVisionCards,
  content,
}) => {
	const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "0px 0px 25% 0px" });

  // The rich text uses "Heading 5" for its visual style, but it follows an
  // <h2>, which skips levels (a11y heading-order failure). Demote to <h3>;
  // the SCSS styles .ecosystem--content h3 to look identical.
  const leveledContent = React.useMemo(
    () =>
      content?.map((node: any) =>
        node.html
          ? { ...node, html: node.html.replace(/<(\/?)h5>/g, "<$1h3>") }
          : node
      ),
    [content]
  );

  return (
		<section className="ecosystem">
			<div className="wrapper">
				<SplitTextAnimation
					htmlElement={<p ref={ref} className="ecosystem--subheader">{subheader}</p>}
					isInView={isInView}
				/>
				<SplitTextAnimation
					htmlElement={<h2 ref={ref} className="ecosystem--header">{header}</h2>}
					isInView={isInView}
				/>
				<div className="ecosystem--card-container">
					{ecosystemCards.map((card: ContentfulCardWithImageType, i) =>
						<EcosystemCard key={`${card.title}-${i}`} card={card}/>
					)}
				</div>
				<div className="ecosystem--mv-card-container">
						{missionAndVisionCards.map((card: ContentfulCardWithImageType, i) => 
							<MissionAndVisionCard key={`${card.title}-${i}`} card={card}/>
						)}
				</div>
				<SplitTextAnimation
					htmlElement={
					<ContentManager
						content={leveledContent}
						className="ecosystem--content"
						style={{
							transform: isInView ? "none" : `translateY(20px)`,
							opacity: isInView ? 1 : 0,
							transition: "all 0.5s cubic-bezier(0.17, 0.55, 0.55, 1)",
						}}
					/>
				}
				isInView={isInView}
			/>
			</div>
		</section>
	)
}
